import { useState } from 'react'

import type { LibraryDocument, LibraryFolder } from '../../api/library'
import { DRAGGED_DOCUMENT, FolderShape, Picture, asDate, asSize, goneFrom } from './Tiles'
import type { View } from './place'

/**
 * The shelf as rows: the same things, close enough together to be compared.
 *
 * A tile answers "what is this" better than anything else and answers "which of these four is the
 * newest" not at all — it shows every thing well and no two things beside each other. These two views
 * are for the second question, and they are the same content and the same actions: a row opens what a
 * tile opens, takes a dropped document where a tile takes one, and drags where a tile drags.
 *
 * **A row still shows its content.** The picture is small and it is still the picture — a first page,
 * a photograph — because a row of grey file icons with names beside them is the thing MyLib exists not
 * to be, and being denser is no licence for it.
 *
 * *List* is the picture and the name. *Details* adds the three facts somebody compares by — kind, size,
 * date — as columns. On a phone those columns sit under the name instead of beside it, because a shelf
 * nobody can read without scrolling sideways is not a denser view, it is a broken one.
 */

export interface DocumentRowProps {
  readonly document: LibraryDocument
  readonly view: View
  readonly open: boolean
  readonly onOpen: () => void
  readonly onDragStart: () => void
  readonly onDragEnd: () => void
}

export function DocumentRow({
  document,
  view,
  open,
  onOpen,
  onDragStart,
  onDragEnd,
}: DocumentRowProps) {
  return (
    <li
      className="row"
      draggable
      onDragStart={(event) => {
        // The same payload a tile puts on the clipboard, for the same reason: a drag that names
        // nothing is filled in from the picture inside it and arrives at the file drop as an upload.
        event.dataTransfer.setData(DRAGGED_DOCUMENT, document.id)
        event.dataTransfer.setData('text/plain', document.title)
        event.dataTransfer.effectAllowed = 'move'
        onDragStart()
      }}
      onDragEnd={onDragEnd}
    >
      <button
        type="button"
        className="row__open"
        aria-current={open ? 'true' : undefined}
        onClick={onOpen}
      >
        <span className="row__picture">
          {/* Keyed by the content, so an edited document asks for its picture again — as on a tile. */}
          <Picture key={document.contentId} document={document} />
        </span>
        <span className="row__name">
          {document.title}
          {document.goneFromSource && <span className="row__gone">{goneFrom(document)}</span>}
        </span>
        {view === 'details' ? (
          <>
            <span className="row__cell row__cell--kind">
              <span className="row__column" aria-hidden="true">
                Kind
              </span>
              {document.kind === '' ? 'File' : document.kind}
            </span>
            <span className="row__cell row__cell--size">
              <span className="row__column" aria-hidden="true">
                Size
              </span>
              {asSize(document.sizeInBytes)}
            </span>
            <span className="row__cell row__cell--date">
              <span className="row__column" aria-hidden="true">
                Date
              </span>
              {asDate(document.takenOverAt)}
            </span>
          </>
        ) : (
          <span className="row__facts">
            <span>{document.kind === '' ? 'File' : document.kind}</span>
            <span aria-hidden="true">·</span>
            <span>{asSize(document.sizeInBytes)}</span>
          </span>
        )}
      </button>
    </li>
  )
}

export interface FolderRowProps {
  readonly folder: LibraryFolder
  readonly view: View
  readonly onOpen: () => void
  readonly offering: boolean
  readonly onDrop: (document: string) => void
}

/** A folder as a row, and — while something is being dragged — a place to drop it, exactly as a tile. */
export function FolderRow({ folder, view, onOpen, offering, onDrop }: FolderRowProps) {
  const [over, setOver] = useState(false)

  return (
    <li
      className={offering && over ? 'row row--folder row--taking' : 'row row--folder'}
      onDragOver={(event) => {
        if (event.dataTransfer.types.includes(DRAGGED_DOCUMENT)) {
          event.preventDefault()
          event.dataTransfer.dropEffect = 'move'
          setOver(true)
        }
      }}
      onDragLeave={() => {
        setOver(false)
      }}
      onDrop={(event) => {
        const moved = event.dataTransfer.getData(DRAGGED_DOCUMENT)
        if (moved !== '') {
          event.preventDefault()
          setOver(false)
          onDrop(moved)
        }
      }}
    >
      <button type="button" className="row__open" onClick={onOpen}>
        <span className="row__picture row__picture--folder" aria-hidden="true">
          <FolderShape />
        </span>
        <span className="row__name">{folder.name}</span>
        {view === 'details' ? (
          <>
            <span className="row__cell row__cell--kind">
              <span className="row__column" aria-hidden="true">
                Kind
              </span>
              Folder
            </span>
            <span className="row__cell row__cell--size">
              {folder.documents === 0
                ? 'Empty'
                : `${folder.documents} document${folder.documents === 1 ? '' : 's'}`}
            </span>
            <span className="row__cell row__cell--date">{asDate(folder.updatedAt)}</span>
          </>
        ) : (
          <span className="row__facts">
            {folder.documents === 0
              ? 'Empty'
              : `${folder.documents} document${folder.documents === 1 ? '' : 's'}`}
          </span>
        )}
      </button>
    </li>
  )
}

export interface TrashRowProps {
  readonly count: number
  readonly view: View
  readonly onOpen: () => void
}

/** The way into the trash, in a row view. Shown while there is something in it, as beside the tiles. */
export function TrashRow({ count, view, onOpen }: TrashRowProps) {
  return (
    <li className="row row--folder row--trash">
      <button type="button" className="row__open" onClick={onOpen}>
        <span className="row__picture row__picture--folder" aria-hidden="true">
          <svg viewBox="0 0 48 36" fill="none" stroke="currentColor" strokeWidth="1.6">
            <path d="M10 10h28l-2.5 22a2 2 0 0 1-2 1.8H14.5a2 2 0 0 1-2-1.8z" />
            <path d="M7 10h34M19 7h10M20 17v11M28 17v11" />
          </svg>
        </span>
        <span className="row__name">Trash</span>
        {view === 'details' ? (
          <>
            <span className="row__cell row__cell--kind" />
            <span className="row__cell row__cell--size">
              {count === 1 ? '1 thing' : `${count} things`}
            </span>
            <span className="row__cell row__cell--date" />
          </>
        ) : (
          <span className="row__facts">{count === 1 ? '1 thing' : `${count} things`}</span>
        )}
      </button>
    </li>
  )
}

export interface NamingRowProps {
  readonly initial: string
  readonly view: View
  readonly onDone: (name: string) => void
  readonly onGiveUp: () => void
}

/**
 * A folder being named, in a row view — the same thing [NamingTile] is, in this shape.
 *
 * Not a form, for the same reason: the row appears where the folder is going to sit, the name is typed
 * onto it, Enter or clicking elsewhere keeps it, Escape or emptiness drops it.
 */
export function NamingRow({ initial, view, onDone, onGiveUp }: NamingRowProps) {
  const [name, setName] = useState(initial)
  const [settled, setSettled] = useState(false)

  const keep = () => {
    if (settled) {
      return
    }
    setSettled(true)
    const wanted = name.trim()
    if (wanted === '') {
      onGiveUp()
    } else {
      onDone(wanted)
    }
  }

  return (
    <li className="row row--folder row--naming">
      <span className="row__picture row__picture--folder" aria-hidden="true">
        <FolderShape />
      </span>
      <input
        className="row__field"
        autoFocus
        aria-label="Name of the new folder"
        placeholder="Folder name"
        value={name}
        onChange={(event) => {
          setName(event.target.value)
        }}
        onBlur={keep}
        onKeyDown={(event) => {
          if (event.key === 'Enter') {
            keep()
          }
          if (event.key === 'Escape') {
            setSettled(true)
            onGiveUp()
          }
        }}
      />
      {view === 'details' && <span className="row__cell row__cell--kind">Folder</span>}
      <span className="row__facts">Enter keeps it, Escape drops it</span>
    </li>
  )
}

/**
 * The column names of the details view, over the rows they name.
 *
 * **Text and not controls.** Sorting is the chip row above, where it is for every view — a clickable
 * column heading is a gesture with no visible counterpart, and it would mean the library sorted in one
 * place in one view and in another place in the other two.
 */
export function Columns() {
  return (
    <li className="row row--columns" aria-hidden="true">
      <span className="row__picture" />
      <span className="row__name">Name</span>
      <span className="row__cell row__cell--kind">Kind</span>
      <span className="row__cell row__cell--size">Size</span>
      <span className="row__cell row__cell--date">Date</span>
    </li>
  )
}
